import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { FolderIcon, ArrowLeftIcon, RectangleStackIcon, TrashIcon } from '@heroicons/react/24/outline';
import { projectsAPI } from '../../services/api';
import DeleteProjectModal from '../DeleteProjectModal';
import { standardLabel } from '../../utils/labels';

function projectSidebarTitle(project, isLoading, isError) {
  if (isLoading) return 'Loading…';
  if (isError) return 'Project unavailable';
  return project?.name || 'Unknown project';
}

// 19.3 keeps the main app's ProjectLayout shell verbatim (256px sidebar, same
// nav-item recipe, owner-only danger zone) but swaps its nav items for this
// app's project pages, and renders <Outlet/> instead of taking a `children`
// prop - 19.5's route table nests ProjectDashboardPage etc. under this layout
// with React Router v6 child routes rather than the main app's manual
// navigate()-and-render pattern.
export default function ProjectLayout() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: project, isLoading, isError, error } = useQuery(
    ['project', projectId],
    () => projectsAPI.get(projectId),
    {
      enabled: !!projectId,
      select: (response) => response.data,
      retry: 1,
    }
  );

  const isOwner = project?.my_role === 'owner';

  // One destination per project: its Videos (RUNLOG V8; the 2026-09-25 brief, section 4).
  // The project-level Review and Reports pages were lists of the same videos filtered by
  // pending findings or by finished runs, each linking into the same per-video tabs; the
  // Videos list now carries each video's review progress, and every video opens on its own
  // Overview / Review Queue / Report tabs. /review and /reports redirect here (App.js).
  const videosPath = `/projects/${projectId}`;
  const videosCurrent =
    location.pathname === videosPath || location.pathname.startsWith(`${videosPath}/videos`);

  // Deep inside a video (Overview -> Review queue -> Report, three routes down), "Back to
  // Projects" used to hard-code navigate('/projects') no matter how the user got to this
  // project, so Back always dumped them at the global list instead of wherever they actually
  // came from. The main app's ProjectLayout solves this by reading location.state.from
  // (set by whichever page links into a project from a non-default place, e.g.
  // OrganizationDetailPage) and falling back to /projects when nothing set it - this mirrors
  // that exactly. The nav buttons below pass the current location.state through their own
  // navigate() calls (like the main app's `state: location.state`) so that value survives
  // moving between this project's own pages instead of being dropped the moment the user
  // clicks anything; VideoHeaderStrip.js does the same for its Back-to-videos and tab links,
  // since those are the other navigation points between here and "deep inside a video".
  const backTarget = location.state?.from || '/projects';

  return (
    // QA-36: print:bg-white because with "Background graphics" on (or page.pdf's
    // printBackground) Chrome paints this wrapper's #F8F8FF across the whole A4 sheet -
    // only <body> and the report body went white. The Tailwind print variant, not an
    // index.css `.min-h-screen` override: it sits on the element it changes, it is what
    // this shell already uses for print (print:hidden on the sidebar), and a global rule
    // keyed on a layout utility would silently re-tint any future wrapper that dropped
    // the class. AppShell's root in App.js carries the same.
    <div data-testid="project-layout-root" className="min-h-screen bg-[#F8F8FF] print:bg-white flex">
      <div className="w-64 bg-white shadow-sm border-r border-gray-200 flex-shrink-0 print:hidden">
        <div className="p-6">
          <button
            type="button"
            onClick={() => navigate(backTarget)}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Projects
          </button>

          <div className="mb-8">
            <div className="flex items-center mb-2">
              <FolderIcon className="h-8 w-8 text-blue-500 mr-3" />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-semibold text-gray-900 truncate">
                  {projectSidebarTitle(project, isLoading, isError)}
                </h1>
                {/* ProjectSummary/ProjectWithDetails carry a flat standard_name
                    string (D25, no members/orgs), unlike the main app's nested
                    project.standard.{name,version}. */}
                {project?.standard_name && (
                  <p className="text-sm text-gray-500 truncate">
                    {standardLabel(project.standard_name, project.standard_version)}
                  </p>
                )}
              </div>
            </div>
            {project?.description && (
              <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
            )}
            {isError && (
              <p className="mt-2 text-xs text-red-600">
                {error?.response?.data?.detail || 'Could not load this project.'}
              </p>
            )}
          </div>

          <nav className="space-y-1">
            <button
              type="button"
              onClick={() => navigate(videosPath, { state: location.state })}
              disabled={isError}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
                videosCurrent
                  ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-[#F8F8FF]'
              }`}
            >
              <RectangleStackIcon className={`mr-3 h-5 w-5 ${videosCurrent ? 'text-blue-500' : 'text-gray-400'}`} />
              {/* Videos and photo sets share this list (Anshul, 2026-09-30: "Videos & photos"). */}
              Videos &amp; photos
            </button>
          </nav>

          {isOwner && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-2">
                Danger zone
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Delete this project and everything inside it — videos, photos, runs and findings.
              </p>
              <button
                type="button"
                data-testid="project-delete-trigger"
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center w-full px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete project
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 pb-24 min-w-0">
        {isError ? (
          <div className="p-6">
            <div className="max-w-lg rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
              {error?.response?.data?.detail || 'This project could not be loaded. You may not have access.'}
            </div>
            <button
              type="button"
              onClick={() => navigate(backTarget)}
              className="mt-4 text-sm text-blue-600 hover:underline"
            >
              ← Back to Projects
            </button>
          </div>
        ) : (
          <Outlet />
        )}
      </div>

      {showDeleteModal && project && (
        <DeleteProjectModal
          project={project}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => navigate('/projects')}
        />
      )}
    </div>
  );
}
